import { checkSession } from "@features/auth";
import { useUserInfo } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { cn } from "@shadcn/lib/utils";
import { preloadQuery } from "@relay";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { PackageCheck, PackagePlus, type LucideIcon } from "lucide-react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { actionWarenannahmeQuery } from "./__generated__/actionWarenannahmeQuery.graphql";

export const query = graphql`
	query actionWarenannahmeQuery($hubName: String!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			organization {
				id
			}
			serviceProviders {
				id
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/warenannahme/action")({
	beforeLoad: () => {
		document.title = "bacehub | Warenannahme";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/warenannahme/action` },
			});
		}
		return preloadQuery<actionWarenannahmeQuery>(ctx.context.relayEnvironment, query, { hubName: ctx.params.hubMetaName });
	},
});

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMetaName } = Route.useParams();
	const { hubMeta } = usePreloadedQuery<actionWarenannahmeQuery>(query, Route.useLoaderData());
	const organizationId = useUserInfo()?.organization?.id;
	// The service providers of the hub deliver into it; the hub owner only collects.
	const isHubOwner = organizationId === hubMeta.organization.id;
	const canDeliver = Boolean(organizationId && hubMeta.serviceProviders.some((provider) => provider.id === organizationId));

	return (
		<PageContainer>
			<AppHeader service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6">
				<p className="text-sm text-muted-foreground">
					<T>{i18n.WA_ACTION_TITLE()}</T>
				</p>
				{isHubOwner ? null : (
					<ActionCard
						to="/$hubMetaName/warenannahme/deliver"
						hubMetaName={hubMetaName}
						icon={PackagePlus}
						title={i18n.WA_ACTION_DELIVER()}
						description={
							canDeliver
								? i18n.WA_ACTION_DELIVER_DESC()
								: organizationId
									? i18n.WA_ACTION_DELIVER_PROVIDER_ONLY()
									: i18n.WA_ACTION_DELIVER_ORG_ONLY()
						}
						disabled={!canDeliver}
					/>
				)}
				<ActionCard
					to="/$hubMetaName/warenannahme/pickup"
					hubMetaName={hubMetaName}
					icon={PackageCheck}
					title={i18n.WA_ACTION_COLLECT()}
					description={i18n.WA_ACTION_COLLECT_DESC()}
				/>
			</div>
		</PageContainer>
	);
}

function ActionCard(props: {
	to: "/$hubMetaName/warenannahme/deliver" | "/$hubMetaName/warenannahme/pickup";
	hubMetaName: string;
	icon: LucideIcon;
	title: string;
	description: string;
	disabled?: boolean;
}) {
	const Icon = props.icon;
	const content = (
		<>
			<Icon className="size-12 stroke-[1.25]" />
			<div className="flex flex-col items-center gap-1 text-center">
				<p className="text-2xl font-breit font-medium">
					<T>{props.title}</T>
				</p>
				<p className="text-sm text-muted-foreground">
					<T>{props.description}</T>
				</p>
			</div>
		</>
	);
	const className = cn(
		"flex min-h-[296px] flex-col items-center justify-center gap-6 rounded-[14px] border-2 bg-background px-6 py-12 shadow-sm transition-colors",
		props.disabled ? "opacity-50" : "hover:shadow-md active:bg-muted/40"
	);
	if (props.disabled) {
		return (
			<div className={className} aria-disabled="true">
				{content}
			</div>
		);
	}
	return (
		<Link to={props.to} params={{ hubMetaName: props.hubMetaName }} className={className}>
			{content}
		</Link>
	);
}
